import { createPinia, setActivePinia } from 'pinia';
import App from './App.vue';
import { createApp } from 'vue';
import { useUserStore } from './stores/userStore';
import router, { createRouterPlugin } from './router/index';
import { useGameAudioStore } from './stores/gameAudioStore';
import { useUIStore } from './stores/uiStore';
import { useTimerStore } from './stores/timerStore';
import VueGameAnalytics from 'vue3-game-analytics';
import { flare } from '@flareapp/js';
import { ServiceWorkerSecurity } from './utils/serviceWorkerSecurity';
import { initializeZoomPrevention } from './utils/zoomPrevention';
import './utils/simpleGamesDebug';

import { ErrorReporter } from './utils/errorReporting';
import { setupInteractionBreadcrumbTracking } from './utils/analytics/breadcrumbs';
import { createAnalyticsEventTransformer } from './utils/analytics/enrichment';
import {
  setAnalyticsTrackEvent,
  type AnalyticsTrackEventFn,
} from './utils/analytics/track';

import './css/app.scss';

if (import.meta.env.MODE === 'production') {
  flare.light();
}

// Clear any cached analytics events from previous sessions
function clearAnalyticsCache() {
  try {
    // Clear localStorage keys that might contain analytics data
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.includes('analytics') ||
          key.includes('game-events') ||
          key.includes('vue-game-analytics'))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Clear sessionStorage as well
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (
        key &&
        (key.includes('analytics') ||
          key.includes('game-events') ||
          key.includes('vue-game-analytics'))
      ) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to clear analytics cache:', error);
  }
}

async function initializeApp() {
  // Initialize zoom prevention system first (replaces all the manual zoom prevention code)
  initializeZoomPrevention();

  // Suppress third-party audio library errors (Tone.js InvalidStateError)
  const originalError = window.console.error;
  window.console.error = (...args: unknown[]) => {
    const errorMessage = args.join(' ');
    // Filter out known non-fatal third-party errors
    if (
      errorMessage.includes('InvalidStateError') ||
      errorMessage.includes('get-native-context') ||
      errorMessage.includes('audio-node-constructor') ||
      errorMessage.includes('ToneBufferSource') ||
      errorMessage.includes('OneShotSource')
    ) {
      return; // Suppress these specific errors
    }
    originalError.apply(console, args);
  };

  // Handle uncaught exceptions from Tone.js
  window.addEventListener('error', (event) => {
    const errorMessage = event.message || '';
    const filename = event.filename || '';

    // Suppress Tone.js audio library errors
    if (
      errorMessage.includes('InvalidStateError') ||
      filename.includes('get-native-context') ||
      filename.includes('audio-node-constructor') ||
      filename.includes('Gain.ts') ||
      filename.includes('ToneAudioNode.ts') ||
      filename.includes('OneShotSource.ts') ||
      filename.includes('ToneBufferSource.ts') ||
      filename.includes('Context.ts') ||
      filename.includes('Timeline.ts') ||
      filename.includes('Emitter.ts')
    ) {
      event.preventDefault();
      // Prevent downstream error handlers (including analytics) from recording this noise.
      event.stopImmediatePropagation();
      return false;
    }
  });

  // Initialize ServiceWorker security before anything else
  ServiceWorkerSecurity.getInstance();

  // Clear any cached analytics events first
  clearAnalyticsCache();

  const app = createApp(App);
  const pinia = createPinia();

  // Use the router plugin
  pinia.use(createRouterPlugin());

  app.use(pinia);
  app.use(router);

  // Make this Pinia instance active so stores can be used outside components
  setActivePinia(pinia);

  // Set up a friendly app update overlay when the service worker updates.
  // Updates only trigger a reload when the user is on the Home or Login page.
  // A short cooldown prevents infinite-reload loops.
  if ('serviceWorker' in navigator) {
    const uiStore = useUIStore();
    const timerStore = useTimerStore();

    const SW_RELOAD_TS_KEY = 'sw-update-reload-ts';
    const SW_RELOAD_COOLDOWN = 30 * 1000; // 30 seconds — just enough to break loops
    let pendingReload = false;

    function hasRecentlyReloaded(): boolean {
      const timestamp = sessionStorage.getItem(SW_RELOAD_TS_KEY);
      if (!timestamp) return false;
      return Date.now() - parseInt(timestamp, 10) < SW_RELOAD_COOLDOWN;
    }

    function isOnSafePage(): boolean {
      const name = router.currentRoute.value.name;
      return name === 'home' || name === 'login';
    }

    function performUpdateReload(): void {
      if (uiStore.isUpdatingApp) return;

      console.warn('[SW Update] Performing update reload');
      uiStore.startAppUpdate(
        "We're updating your games. This will only take a moment."
      );
      timerStore.startTimer(3);
      sessionStorage.setItem(SW_RELOAD_TS_KEY, String(Date.now()));

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    // Attempt to apply a pending update, respecting the cooldown and
    // home-page gate. Returns true if the reload was triggered.
    function tryApplyUpdate(): boolean {
      if (hasRecentlyReloaded()) {
        console.warn('[SW Update] Skipping — already reloaded recently');
        return false;
      }
      if (!isOnSafePage()) {
        console.warn('[SW Update] Deferring — user not on a safe page');
        pendingReload = true;
        return false;
      }
      performUpdateReload();
      return true;
    }

    // When the user navigates to a safe page, apply any deferred update.
    router.afterEach((to) => {
      if (pendingReload && (to.name === 'home' || to.name === 'login')) {
        pendingReload = false;
        tryApplyUpdate();
      }
    });

    console.warn('[SW Update] Setting up service worker update detection...');

    // Listen for registration updates
    navigator.serviceWorker.ready.then((registration) => {
      console.warn('[SW Update] Service worker ready');

      // A waiting worker already exists on page load
      if (registration.waiting && navigator.serviceWorker.controller) {
        console.warn('[SW Update] Found waiting service worker on load');
        tryApplyUpdate();
      }

      // Listen for newly-installed workers
      registration.addEventListener('updatefound', () => {
        console.warn('[SW Update] Update found event fired');
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          console.warn('[SW Update] Worker state changed to:', newWorker.state);
          if (
            newWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            tryApplyUpdate();
          }
        });
      });
    });

    // When the new SW takes control, trigger the reload (with guards).
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.warn('[SW Update] Controller change detected');
      tryApplyUpdate();
    });

    // Periodic fallback check — every 30 minutes (browser already checks on
    // navigation and every 24 h; 60 s was driving the reload loop).
    setInterval(
      async () => {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            console.warn('[SW Update] Periodic check for updates...');
            await registration.update();
          }
        } catch (error) {
          console.error('[SW Update] Error checking for updates:', error);
        }
      },
      30 * 60 * 1000
    );
  }

  // Determine the analytics API endpoint based on environment
  const getAnalyticsEndpoint = () => {
    const apiDomain = import.meta.env.VITE_API_DOMAIN;
    const apiPort = import.meta.env.VITE_API_PORT;
    const devMode = import.meta.env.VITE_DEV_MODE === 'true';

    if (devMode) {
      return '/api/analytics/events';
    }

    if (apiDomain) {
      return apiPort
        ? `${apiDomain}:${apiPort}/api/analytics/events`
        : `${apiDomain}/api/analytics/events`;
    }

    return '/api/analytics/events';
  };

  // Initialize game analytics with default configuration
  app.use(VueGameAnalytics, {
    gameId: 'default', // Will be overridden by the analytics transformer when in-game
    playId: 'default', // Will be overridden by the analytics transformer when in-game
    userId: 'manual', // Not used by vue3-game-analytics directly; we attach userId in metadata
    apiEndpoint: getAnalyticsEndpoint(),
    debug: false, // Set to false to prevent debug output
    visibleTracking: false, // Disable visual indicators to reduce noise
    verboseLogging: false, // Disable verbose console logging to reduce noise
    trackClicks: false,
    trackTouches: false,
    trackKeyboard: false, // Avoid capturing keystrokes (e.g. login/password fields)
    batchSize: 25,
    flushInterval: 60000, // 1 minute
    consentRequired: false,
    respectDoNotTrack: false, //TODO: EU need to ask for tracking consent for GDPR
    eventTransformer: createAnalyticsEventTransformer(router),
  });

  // Expose analytics tracking to non-component modules (e.g. ErrorReporter)
  setAnalyticsTrackEvent(
    (
      app.config.globalProperties.$gameAnalytics as {
        trackEvent: AnalyticsTrackEventFn;
      }
    ).trackEvent
  );

  // Capture lightweight interaction/route breadcrumbs for better error context.
  setupInteractionBreadcrumbTracking(router);

  // Capture Vue component errors with context.
  app.config.errorHandler = (err, instance, info) => {
    try {
      const vueType = instance?.type as Record<string, unknown> | undefined;
      const componentName =
        (vueType?.name as string) || (vueType?.__file as string) || 'Vue';

      ErrorReporter.report(
        err,
        {
          component: componentName,
          action: 'vue_errorHandler',
          additionalData: { info },
        },
        'error'
      );
    } catch (reportError) {
      console.error('Failed to report Vue error:', reportError);
    }
  };

  // const gameAudioStore = useGameAudioStore();
  // Initialize core systems
  // await Promise.all([
  //   gameAudioStore.initializeAudio(), // Setup audio system defaults
  //   // useUserStore().checkAuth(),
  // ]);

  // Mount the app only after everything is initialized
  app.mount('#app');
}

initializeApp().catch(console.error);
